import type { Feature } from 'geojson';
import { useEffect, useMemo } from 'react';
import { badgeIcon, registerIcons } from './icons';
import { fc, GeoJsonLayer } from './layers';
import { useMap } from './map-view';

export interface LineStop {
  id: string;
  name: string;
  position: [number, number];
  /** Closest point on the route (drawn as a dashed tie when the stop is away from it). */
  onRoute?: [number, number];
  /** Something is wrong with this stop (far from the route, out of order). */
  warn?: boolean;
}

export const HIGHLIGHT = '#f5b700';
const WARN = '#d97706';

/**
 * A line's stops as numbered badges in travel order (1, 2, 3 …), drawn above the route, with names
 * from zoom 14 and a dashed tie to the route for stops that are not on it.
 */
export function LineStopsLayer({
  stops,
  color,
  highlight,
  onHover,
  onClick,
}: {
  stops: LineStop[];
  color: string;
  highlight?: string | null;
  onHover?: (props: Record<string, unknown> | null) => void;
  onClick?: (props: Record<string, unknown>) => void;
}) {
  const map = useMap();
  useEffect(() => {
    if (map) registerIcons(map);
  }, [map]);

  const ties = useMemo(
    () =>
      fc(
        stops
          .filter((s) => s.onRoute)
          .map(
            (s): Feature => ({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: [s.position, s.onRoute!] },
              properties: { color: s.warn ? WARN : '#64748b' },
            }),
          ),
      ),
    [stops],
  );
  const badges = useMemo(
    () =>
      fc(
        stops.map(
          (s, i): Feature => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: s.position },
            properties: {
              id: s.id,
              name: s.name,
              lng: s.position[0],
              lat: s.position[1],
              icon: badgeIcon(s.id === highlight ? HIGHLIGHT : s.warn ? WARN : color, String(i + 1)),
              top: s.id === highlight ? 2 : s.warn ? 1 : 0,
            },
          }),
        ),
      ),
    [stops, color, highlight],
  );

  return (
    <>
      <GeoJsonLayer
        data={ties}
        layers={[
          {
            type: 'line',
            paint: { 'line-color': ['get', 'color'], 'line-width': 1.5, 'line-dasharray': [2, 2] },
          },
        ]}
      />
      <GeoJsonLayer
        data={badges}
        onHover={onHover}
        onClick={onClick}
        layers={[
          {
            type: 'symbol',
            layout: {
              'icon-image': ['get', 'icon'],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'symbol-sort-key': ['get', 'top'],
            },
          },
          {
            type: 'symbol',
            minzoom: 14,
            layout: {
              'text-field': ['get', 'name'],
              'text-size': 11,
              'text-anchor': 'left',
              'text-offset': [1.3, 0],
              'text-optional': true,
            },
            paint: { 'text-color': '#1e293b', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
          },
        ]}
      />
    </>
  );
}

/** Eases the map to `at` whenever it changes (e.g. a stop picked in a list). */
export function FocusOn({ at }: { at?: { position: [number, number]; seq: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (map && at) map.easeTo({ center: at.position, zoom: Math.max(map.getZoom(), 15), duration: 500 });
  }, [map, at]);
  return null;
}
